/**
 * 权限引导对话框组件
 * @description 智能权限引导，将"权限拒绝"转化为"升级引导"
 * @author Claude Code Assistant
 * @date 2025-08-10
 */

<script setup lang="ts">
import type { PermissionGuidance } from '@/types/permission'
import { Check } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

interface Props {
  modelValue: boolean
  guidance?: PermissionGuidance
  featureIcon?: string
  showPreviewSection?: boolean
  onPreviewMode?: () => void
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  featureIcon: 'Lock',
  showPreviewSection: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'upgrade': [path: string]
  'preview': []
  'cancel': []
}>()

const router = useRouter()

// 响应式数据
const dialogVisible = ref(false)
const selectedPath = ref<string>('')
const dontShowAgain = ref(false)
const upgradeLoading = ref(false)

// 计算属性
const primaryColor = computed(() => 'var(--theme-brand-primary, #2e5496)')

const dialogWidth = computed(() => {
  if (props.guidance?.upgradePaths?.length) {
    return '640px'
  }
  return '520px'
})

const selectedUpgradePath = computed(() => {
  if (!selectedPath.value || !props.guidance?.upgradePaths)
    return null
  return props.guidance.upgradePaths.find(path => path.role === selectedPath.value)
})

// 监听器
watch(() => props.modelValue, (newValue) => {
  dialogVisible.value = newValue
  if (newValue) {
    // 对话框打开时初始化数据
    selectedPath.value = props.guidance?.upgradePaths?.[0]?.role || ''
    dontShowAgain.value = false
    upgradeLoading.value = false
  }
})

watch(dialogVisible, (newValue) => {
  emit('update:modelValue', newValue)
})

// 方法
function getRoleName(role: string): string {
  const roleNames: Record<string, string> = {
    designer: '认证设计师',
    manufacturer: '认证制造商',
    admin: '平台管理员',
  }
  return roleNames[role] || role
}

async function handleUpgrade() {
  if (!selectedUpgradePath.value)
    return

  upgradeLoading.value = true

  try {
    // 记录用户选择
    if (dontShowAgain.value) {
      localStorage.setItem(`permission-guidance-${props.guidance?.missingPermission}`, 'hidden')
    }

    // 导航到升级页面
    const upgradePath = selectedUpgradePath.value.path
    await router.push(upgradePath)

    // 发送升级事件
    emit('upgrade', upgradePath)

    // 关闭对话框
    dialogVisible.value = false

    ElMessage.success({
      message: `正在前往${getRoleName(selectedPath.value)}认证页面`,
      duration: 2000,
    })
  }
  catch (error) {
    console.error('导航到升级页面失败:', error)
    ElMessage.error('跳转失败，请稍后重试')
  }
  finally {
    upgradeLoading.value = false
  }
}

function handlePreviewMode() {
  dialogVisible.value = false
  emit('preview')
  props.onPreviewMode?.()

  ElMessage.info({
    message: '已进入预览模式，部分功能受限',
    duration: 2000,
  })
}

function handleCancel() {
  dialogVisible.value = false
  emit('cancel')
}

function handleLearnMore() {
  // 可以打开帮助页面或显示更多信息
  ElMessage.info('您可以在帮助中心了解更多权限相关信息')
}

function handleDialogClosed() {
  // 对话框关闭时的清理工作
  selectedPath.value = ''
  dontShowAgain.value = false
  upgradeLoading.value = false
}
</script>

<template>
  <el-dialog
    v-model="dialogVisible"
    :title="guidance?.title || '解锁更多功能'"
    :width="dialogWidth"
    :center="true"
    :close-on-click-modal="false"
    :show-close="true"
    class="permission-guidance-dialog"
    @closed="handleDialogClosed"
  >
    <!-- 引导内容主体 -->
    <div class="guidance-content">
      <!-- 功能介绍区域 -->
      <div class="feature-intro">
        <div class="intro-icon">
          <el-icon size="48px" :color="primaryColor">
            <component :is="featureIcon" />
          </el-icon>
        </div>
        <h3 class="intro-title">
          {{ guidance?.title || '专业功能' }}
        </h3>
        <p class="intro-description">
          {{ guidance?.description || '该功能需要更高权限才能使用' }}
        </p>
      </div>

      <!-- 功能预览区域 -->
      <div v-if="guidance?.benefitPreview?.length" class="benefit-preview">
        <h4 class="preview-title">
          升级后您将获得：
        </h4>
        <ul class="benefit-list">
          <li v-for="benefit in guidance.benefitPreview" :key="benefit" class="benefit-item">
            <el-icon class="benefit-icon">
              <Check />
            </el-icon>
            <span>{{ benefit }}</span>
          </li>
        </ul>
      </div>

      <!-- 升级路径选择 -->
      <div v-if="guidance?.upgradePaths?.length" class="upgrade-paths">
        <h4 class="paths-title">
          选择您的升级路径：
        </h4>
        <div class="path-options">
          <div
            v-for="path in guidance.upgradePaths"
            :key="path.role"
            class="path-option"
            :class="{ 'path-option--selected': selectedPath === path.role }"
            @click="selectedPath = path.role"
          >
            <div class="path-header">
              <el-radio v-model="selectedPath" :label="path.role" />
              <div class="path-info">
                <h5 class="path-name">
                  {{ getRoleName(path.role) }}
                </h5>
                <span class="path-time">预计用时：{{ path.time }}</span>
              </div>
            </div>
            <div class="path-benefits">
              <span class="benefits-label">特权：</span>
              <span class="benefits-text">{{ path.benefits || '专业功能权限' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 立即体验区域 -->
      <div v-if="showPreviewSection" class="preview-section">
        <el-divider>或者</el-divider>
        <div class="preview-option">
          <h4 class="preview-option-title">
            预览模式体验
          </h4>
          <p class="preview-option-desc">
            以限制模式先体验功能，了解升级后的价值
          </p>
          <el-button
            type="text"
            class="preview-button"
            @click="handlePreviewMode"
          >
            进入预览模式
          </el-button>
        </div>
      </div>
    </div>

    <!-- 对话框底部操作 -->
    <template #footer>
      <div class="dialog-footer">
        <div class="footer-left">
          <el-checkbox
            v-model="dontShowAgain"
            size="small"
          >
            不再提示此功能
          </el-checkbox>
        </div>
        <div class="footer-right">
          <el-button @click="handleCancel">
            稍后再说
          </el-button>
          <el-button
            v-if="selectedUpgradePath"
            type="primary"
            :loading="upgradeLoading"
            @click="handleUpgrade"
          >
            立即升级 ({{ selectedUpgradePath.time }})
          </el-button>
          <el-button
            v-else
            type="primary"
            @click="handleLearnMore"
          >
            了解更多
          </el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<style lang="scss" scoped>
.permission-guidance-dialog {
  :deep(.el-dialog) {
    overflow: hidden;
    border-radius: var(--theme-radius-lg, 8px);
  }

  :deep(.el-dialog__header) {
    padding: var(--theme-spacing-lg, 24px);
    margin: 0;
    color: white;
    background: var(--theme-brand-primary, #2e5496);

    .el-dialog__title {
      font-size: var(--theme-font-size-lg, 18px);
      font-weight: 600;
      color: white;
    }

    .el-dialog__headerbtn {
      .el-dialog__close {
        color: rgb(255 255 255 / 80%);

        &:hover {
          color: white;
        }
      }
    }
  }

  :deep(.el-dialog__body) {
    padding: var(--theme-spacing-xl, 32px);
  }

  :deep(.el-dialog__footer) {
    padding: var(--theme-spacing-lg, 24px) var(--theme-spacing-xl, 32px);
    margin: 0;
    background: var(--theme-background-light, #f5f7fa);
  }

  .guidance-content {
    .feature-intro {
      margin-bottom: var(--theme-spacing-xl, 32px);
      text-align: center;

      .intro-icon {
        margin-bottom: var(--theme-spacing-lg, 24px);
      }

      .intro-title {
        margin: 0 0 var(--theme-spacing-md, 16px) 0;
        font-size: var(--theme-font-size-xl, 20px);
        font-weight: 600;
        color: var(--theme-text-primary, var(--el-text-color-primary));
      }

      .intro-description {
        margin: 0;
        font-size: var(--theme-font-size-base, 14px);
        line-height: 1.6;
        color: var(--theme-text-regular, #606266);
      }
    }

    .benefit-preview {
      margin-bottom: var(--theme-spacing-xl, 32px);

      .preview-title {
        margin: 0 0 var(--theme-spacing-lg, 24px) 0;
        font-size: var(--theme-font-size-lg, 18px);
        font-weight: 600;
        color: var(--theme-text-primary, var(--el-text-color-primary));
      }

      .benefit-list {
        padding: 0;
        margin: 0;
        list-style: none;

        .benefit-item {
          display: flex;
          gap: var(--theme-spacing-sm, 8px);
          align-items: center;
          padding: var(--theme-spacing-sm, 8px) 0;

          .benefit-icon {
            flex-shrink: 0;
            font-size: 16px;
            color: var(--theme-color-success, #67c23a);
          }

          span {
            font-size: var(--theme-font-size-base, 14px);
            line-height: 1.5;
            color: var(--theme-text-regular, #606266);
          }
        }
      }
    }

    .upgrade-paths {
      margin-bottom: var(--theme-spacing-xl, 32px);

      .paths-title {
        margin: 0 0 var(--theme-spacing-lg, 24px) 0;
        font-size: var(--theme-font-size-lg, 18px);
        font-weight: 600;
        color: var(--theme-text-primary, var(--el-text-color-primary));
      }

      .path-options {
        display: flex;
        flex-direction: column;
        gap: var(--theme-spacing-md, 16px);

        .path-option {
          padding: var(--theme-spacing-lg, 24px);
          cursor: pointer;
          border: 2px solid var(--theme-border-color-light, #e4e7ed);
          border-radius: var(--theme-radius-md, 4px);
          transition: all 0.2s ease;

          &:hover {
            background: var(--theme-background-light, #f5f7fa);
            border-color: var(--theme-brand-primary, #2e5496);
          }

          &--selected {
            background: rgb(64 158 255 / 5%);
            border-color: var(--theme-brand-primary, #2e5496);
          }

          .path-header {
            display: flex;
            gap: var(--theme-spacing-md, 16px);
            align-items: center;
            margin-bottom: var(--theme-spacing-sm, 8px);

            .path-info {
              .path-name {
                margin: 0 0 var(--theme-spacing-xs, 4px) 0;
                font-size: var(--theme-font-size-base, 14px);
                font-weight: 600;
                color: var(--theme-text-primary, var(--el-text-color-primary));
              }

              .path-time {
                font-size: var(--theme-font-size-sm, 12px);
                color: var(--theme-text-secondary, var(--el-text-color-secondary));
              }
            }
          }

          .path-benefits {
            font-size: var(--theme-font-size-sm, 12px);
            color: var(--theme-text-regular, #606266);

            .benefits-label {
              font-weight: 600;
            }

            .benefits-text {
              color: var(--theme-color-success, #67c23a);
            }
          }
        }
      }
    }

    .preview-section {
      .preview-option {
        padding: var(--theme-spacing-lg, 24px);
        text-align: center;
        background: var(--theme-background-light, #f5f7fa);
        border-radius: var(--theme-radius-md, 4px);

        .preview-option-title {
          margin: 0 0 var(--theme-spacing-sm, 8px) 0;
          font-size: var(--theme-font-size-base, 14px);
          font-weight: 600;
          color: var(--theme-text-primary, var(--el-text-color-primary));
        }

        .preview-option-desc {
          margin: 0 0 var(--theme-spacing-md, 16px) 0;
          font-size: var(--theme-font-size-sm, 12px);
          color: var(--theme-text-secondary, var(--el-text-color-secondary));
        }

        .preview-button {
          font-size: var(--theme-font-size-base, 14px);
          text-decoration: underline;
        }
      }
    }
  }

  .dialog-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .footer-left {
      .el-checkbox {
        :deep(.el-checkbox__label) {
          font-size: var(--theme-font-size-sm, 12px);
          color: var(--theme-text-secondary, var(--el-text-color-secondary));
        }
      }
    }

    .footer-right {
      display: flex;
      gap: var(--theme-spacing-sm, 8px);
    }
  }
}
</style>
